/**
 * "Supported by your local PWS Partner" tile.
 *
 * Rendered on every suburb landing page whose `region_cluster` maps
 * onto a region with a confirmed active partner. The list of active
 * partners per region is authoritative via `locationTree.REGIONS`.
 *
 * If the suburb has no active partner, or the fetch fails, the tile
 * renders nothing — never a placeholder.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { REGIONS } from "@/data/locationTree";

export default function SupportedByPartnerTile({ regionCluster, suburbName }) {
  const [partner, setPartner] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Look up the region for this cluster, then load the first
      // active partner_slug from the tree. Only one partner is
      // supported per suburb tile in v1 — if a region has multiple
      // partners we show the primary (first entry).
      const region = Object.values(REGIONS).find((r) =>
        (r.clusters || []).some((c) => c.key === regionCluster),
      );
      const partnerSlug = region?.partner_slugs?.[0];
      if (!partnerSlug) return;
      try {
        const { data } = await api.get(`/pws-partner-landing/by-partner-slug/${partnerSlug}`);
        if (!cancelled) setPartner(data);
      } catch {
        // Silent — no tile means no incorrect attribution.
      }
    })();
    return () => { cancelled = true; };
  }, [regionCluster]);

  if (!partner || partner.status !== "published") return null;

  return (
    <section
      className="py-12 bg-gradient-to-br from-slate-900 to-cyan-900 text-white"
      data-testid="supported-by-partner-tile"
    >
      <div className="max-w-5xl mx-auto px-6">
        <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
          {(partner.franchisee_photo_url || partner.franchisee_logo_url) && (
            <img
              src={partner.franchisee_photo_url || partner.franchisee_logo_url}
              alt={partner.franchisee_name || partner.business_name}
              className="w-24 h-24 md:w-28 md:h-28 rounded-2xl object-cover ring-4 ring-white/10 shrink-0"
            />
          )}
          <div className="flex-1">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-cyan-500/20 border border-cyan-400/30 text-cyan-100 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider">
              <ShieldCheck className="w-3 h-3" /> Supported by your local PWS Partner
            </div>
            <h2 className="mt-3 text-2xl md:text-3xl font-bold">{partner.business_name}</h2>
            <div className="mt-1 text-sm text-slate-300 flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5" /> {partner.region_label || "Your area"}
            </div>
            <p className="mt-3 text-sm text-slate-200 max-w-xl">
              {partner.business_name} handles {suburbName || "your suburb"} bookings end-to-end — sample kit delivery, laboratory testing, and follow-up recommendations.
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <Link to={`/book-water-test?p=${partner.partner_slug}`}>
                <Button
                  className="rounded-full bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-bold h-11 px-6"
                  data-testid="supported-by-partner-book"
                >
                  Book with {partner.business_name.split(" ")[0]} <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
              <Link to={`/pws-partner/${partner.slug}`}>
                <Button variant="outline" className="rounded-full border-white/30 text-white hover:bg-white/10 h-11 px-6" data-testid="supported-by-partner-profile">
                  Partner profile
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
